import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import API from '../../../../services/API'
import CompanyLogo from './CompanyLogo'
//@MUI
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Address from './Address'
import LogoColor from './LogoColor'
import { Autocomplete } from '@mui/material'
import { getCurrencySymbol } from '../../../../helpers/currency'

const CompanySettings = () => {
  const [loading, setLoading] = useState(true)
  const [companySettings, setCompanySettings] = useState({})
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)
  const [replyToEmailError, setReplyToEmailError] = useState(false)
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

  useEffect(() => {
    API.get('/settings')
      .then((res) => res.status === 200 && setCompanySettings(res.data))
      .finally(() => setLoading(false))
  }, [])

  const handleSubmitChanges = (e) => {
    e.preventDefault()
    const settings = { ...companySettings }
    delete settings.photograph
    if (settings.replyToEmail && !emailRegex.test(settings.replyToEmail)) {
      setToast({
        message: 'Invalid Reply-To Email Address',
        severity: 'error',
      })
    } else {
      API.put('/site-config', settings).then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Settings Successfully Updated' })
          setOrgConfig((prev) => ({
            ...prev,
            orgConfig: {
              ...prev.orgConfig,
              ...res.data,
            },
          }))
        }
      })
    }
  }

  return loading ? (
    <LinearProgress />
  ) : (
    <Grid container spacing={2} component="form" onSubmit={handleSubmitChanges}>
      <Grid item xs={12} lg={8}>
        <TextField
          label="Company Name"
          fullWidth
          value={companySettings?.orgName ?? ''}
          disabled={loading}
          onChange={(e) =>
            setCompanySettings((prev) => ({ ...prev, orgName: e.target.value }))
          }
          required
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          label="Short Name"
          fullWidth
          value={companySettings?.orgShortName ?? ''}
          helperText="Used for SMS, etc."
          onChange={(e) => {
            const value = e.target.value
              .toUpperCase()
              .replace(/[^A-Z]/g, '')
              .slice(0, 11)
            setCompanySettings((prev) => ({
              ...prev,
              orgShortName: value,
            }))
          }}
          autoComplete="off"
          required
          inputProps={{ maxLength: 11 }}
        />
      </Grid>
      <Grid item xs={12} lg={6} xl={5.5}>
        <TextField
          label="Company Website"
          fullWidth
          value={companySettings?.publicWebsite ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              publicWebsite: e.target.value,
            }))
          }
          helperText="Your public website URL"
          autoComplete="none"
        />
      </Grid>
      <Grid item xs={12} lg={6} xl={5.5}>
        <TextField
          label="Reply To Email"
          fullWidth
          required
          value={companySettings?.replyToEmail ?? ''}
          helperText="What email should replies from system messages (i.e. Shift Assignment or Reference Requests) get sent to?"
          onChange={(e) => {
            setReplyToEmailError(false)
            const value = e.target.value
            setCompanySettings((prev) => ({
              ...prev,
              replyToEmail: value,
            }))
            if (!emailRegex.test(value)) {
              setReplyToEmailError(true)
            }
          }}
          error={replyToEmailError}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6} xl={1}>
        <Autocomplete
          value={getCurrencySymbol(companySettings?.currency)}
          label="Currency"
          onChange={(e, newValue) => {
            setCompanySettings((prev) => ({
              ...prev,
              currency: newValue,
            }))
          }}
          renderInput={(params) => <TextField {...params} label="Currency" />}
          options={['£', '$', '€']}
          disableClearable
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Company Registration Number"
          fullWidth
          value={companySettings?.companyRegistrationNumber ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              companyRegistrationNumber: e.target.value,
            }))
          }
          helperText="Shown on invoice headers"
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="VAT Number"
          fullWidth
          value={companySettings?.vatNumber ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              vatNumber: e.target.value,
            }))
          }
          helperText="Shown on invoice headers"
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Invoice Number Prefix"
          fullWidth
          value={companySettings?.invoicePrefix ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              invoicePrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
            }))
          }
          helperText='Optional — e.g. "ORG" turns invoice 250001 into ORG-250001'
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">Accounting Export</Typography>
        <Typography variant="body2" color="text.secondary">
          Used when exporting invoices as a CSV for your accounting package
          (e.g. Xero).
        </Typography>
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Default Nominal Code"
          fullWidth
          value={companySettings?.defaultNominalCode ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              defaultNominalCode: e.target.value,
            }))
          }
          helperText="Account code for invoice lines without their own nominal code — defaults to 200"
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Tax Type — Standard Rate"
          fullWidth
          value={companySettings?.xeroTaxTypeStandard ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              xeroTaxTypeStandard: e.target.value,
            }))
          }
          helperText='Tax rate names exactly as they appear in your Xero organisation — e.g. "20% (VAT on Income)"'
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Tax Type — Reduced Rate"
          fullWidth
          value={companySettings?.xeroTaxTypeReduced ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              xeroTaxTypeReduced: e.target.value,
            }))
          }
          helperText='Tax rate names exactly as they appear in your Xero organisation — e.g. "5% (VAT on Income)"'
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Tax Type — No VAT"
          fullWidth
          value={companySettings?.xeroTaxTypeNone ?? ''}
          onChange={(e) =>
            setCompanySettings((prev) => ({
              ...prev,
              xeroTaxTypeNone: e.target.value,
            }))
          }
          helperText='Tax rate names exactly as they appear in your Xero organisation — e.g. "No VAT"'
          autoComplete="off"
        />
      </Grid>
      <Address
        companySettings={companySettings}
        setCompanySettings={setCompanySettings}
      />
      <CompanyLogo existingLogo={companySettings?.logo} />
      <LogoColor
        companySettings={companySettings}
        setCompanySettings={setCompanySettings}
      />
      <Grid item xs={12}>
        <Button fullWidth variant="contained" type="submit">
          Submit Changes
        </Button>
      </Grid>
    </Grid>
  )
}

export default CompanySettings
